import type { ConversationDetail, ConversationMessage } from '@/api';

/**
 * At most this many unread messages open expanded — the newest ones. A chain
 * of a hundred unread notices would otherwise open a hundred bodies (a
 * hundred detail requests) and defeat the divider; the older unread stay
 * collapsed, behind it, and stay unread until read.
 */
export const MAX_AUTO_EXPANDED_UNREAD = 5;

/**
 * The messages a conversation opens with expanded (plan/16 §8, Gmail's rule):
 * the newest one, the newest {@link MAX_AUTO_EXPANDED_UNREAD} unread ones, and
 * the one that was opened — so a search result or a notification deep link
 * lands on what it named, even when that is an old, read message in the
 * middle of the stack.
 */
export function defaultExpanded(detail: ConversationDetail, openedId: string | undefined): Set<string> {
  const expanded = new Set<string>();
  const newest = detail.messages.at(-1);
  if (newest !== undefined) expanded.add(newest.id);
  let unread = 0;
  for (let i = detail.messages.length - 1; i >= 0 && unread < MAX_AUTO_EXPANDED_UNREAD; i--) {
    const message = detail.messages[i];
    if (message !== undefined && !message.flags.includes('seen')) {
      expanded.add(message.id);
      unread++;
    }
  }
  if (openedId !== undefined && detail.messages.some((message) => message.id === openedId)) {
    expanded.add(openedId);
  }
  return expanded;
}

/**
 * The members a conversation-level verb acts on: the messages IN THIS FOLDER.
 * A merged Sent message lives in Sent; archiving, deleting, starring or
 * reporting "the conversation" in the Inbox must not reach into Sent
 * (plan/16 §8, "Actions").
 */
export function inFolderMembers(detail: ConversationDetail): ConversationMessage[] {
  return detail.messages.filter((message) => message.inFolder);
}
